import React, { useEffect } from 'react';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import { format } from 'date-fns';
import Image from 'next/legacy/image';

import { Blog, BlogsResponse, BlogWithoutId } from 'apolloClient/types/Blog';
import {
  defaultImage,
  defaultImageHeight,
  defaultImageWidth,
} from 'src/constants/constants';
import { Entity, ResponseCollection } from 'apolloClient/types/common';
import { DEFAULT_REVALIDATE_INTERVAL, ORIGIN } from 'src/constants';
import TagLinks from 'components/Blog/TagLinks';
import { blogView } from 'src/ga';
import { usePopularBlogs } from 'components/Blog/usePopularBlogs';

import Breadcrumbs from 'components/Breadcrumbs';
import ContactUs from 'components/Article/ContactUs';
import SearchBlogPage from 'components/Blog/SearchBlogPage';
import RelatedArticles from 'components/RelatedArticles';
import InsidersGuides from 'components/Blog/InsidersGuides';
import Articles from 'components/Articles';
import SignUpSection from 'components/Blog/SignUpSection';
import PopularArticles from 'components/Article/PopularArticles';
import PageBuilderContentParser from 'components/PageBuilderContentParser';
import { client } from 'apolloClient/client';
import {
  BLOG_ITEMS_LIST,
  LOAD_ARTICLE,
  LOAD_BLOGS,
} from 'apolloClient/queries/blogs';
import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from 'lib/flattenStrapiBulkDataItems';
import { Metadata } from 'components/Metadata';
import ErrorBoundary from 'components/ErrorBoundary';
import { getStrapiMedia } from 'lib/getStrapiMedia';
import Header from 'components/layouts/Header/Header';
import {
  Agent,
  AgentsResponse,
  DefaultAgentsResponse,
  StrapiAgent,
} from 'apolloClient/types/Agents';
import {
  LOAD_AGENTS_FOR_EXPERTS_SECTION,
  LOAD_COMMON_AGENT,
} from 'apolloClient/queries/agents';
import { notfoundRedirect } from 'constants/notFoundParams';
import Link from 'next/link';
import { StrapiMediaInfo } from 'apolloClient/types';
import {
  getSmallImageUrlFromImage,
  getMediumImageUrlFromImage,
  getThumbnailImageUrlFromImage,
} from 'lib/getImages';
import LineBetweenSections from 'components/LineBetweenSections';
import GoogleAnalytics from 'src/ga/grouping';
import { Theme, useMediaQuery } from '@mui/material';

interface ArticleInterface {
  blogs: Blog[];
  article: Blog;
  commonAgent: StrapiAgent;
  experts?: Agent[];
}

type MainImageAdditionalProps = {
  src: string;
  blurDataURL: string;
  width: number;
  height: number;
};

function getMainImageProps(image: StrapiMediaInfo): MainImageAdditionalProps {
  const mediumImage = image.formats?.medium || image;
  const mediumImageUrl = getMediumImageUrlFromImage(image);
  const blurDataURL = getThumbnailImageUrlFromImage(image) || defaultImage;

  if (mediumImage?.url) {
    return {
      src: mediumImageUrl || defaultImage,
      width: mediumImage.width,
      height: mediumImage.height,
      blurDataURL,
    };
  }

  return {
    src: image.url,
    width: image.width,
    height: image.height,
    blurDataURL,
  };
}

const Article: React.FC<ArticleInterface> = ({
  article,
  blogs,
  commonAgent,
  experts,
}) => {
  const {
    title,
    author,
    createdBy,
    blogTags,
    mainImage,
    pageContent,
    shortDescription,
    isFullWidth,
    seo,
    publishedDate,
  } = article;
  const { asPath } = useRouter();
  const { popularBlogs } = usePopularBlogs();

  useEffect(() => {
    blogView(
      asPath,
      (blogTags?.data || []).reduce<Array<string>>((acc, { attributes }) => {
        if (attributes.label) {
          acc.push(attributes.label);
        }

        return acc;
      }, [])
    );
  }, []);

  const image = flattenStrapiDataItem(mainImage?.data);
  const formattedDate = format(new Date(publishedDate), 'MMMM dd, yyyy');
  const writtenBy = author || createdBy;

  const metaTitle = seo?.metaTitle || title || 'Blog';
  const metaDescription = seo?.metaDescription || shortDescription || '';
  const metaKeywords = seo?.keywords || '';
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  return (
    <>
      <Metadata
        {...{
          title: `${metaTitle} | CondoBlackBook Blog`,
          description: metaDescription,
          keywords: metaKeywords,
          url: `${ORIGIN}${asPath}`,
          noCanonical: true,
          image: seo?.metaImage
            ? getStrapiMedia(seo?.metaImage?.data?.attributes)
            : mainImage
            ? getStrapiMedia(mainImage?.data?.attributes)
            : undefined,
        }}
      />
      <GoogleAnalytics pageGroup={'Blog'} />
      <Header />
      <SearchBlogPage showSignUp={true} />
      <section id="blog-ga4" className="pt-[21px] lg:px-[114px] lg:mt-[-51px]">
        {isDesktop && <Breadcrumbs blogPage="Blog" articlePage={title} />}
        <div className={`container pt-[22px]`}>
          <TagLinks tags={blogTags} />
          <h1
            className="font-sohneKraftig pt-[9px] pb-[10px] md:pb-[15px] lg:pt-[4px] lg:pb-[19px] text-18-23-0.3 md:text-32-42-0.3 lg:w-[60.5%]"
            itemProp="name"
          >
            {title}
          </h1>
          <p className="mb-[11px] lg:pl-[4px] pb-[21px] md:pb-[24px] lg:pb-[37px] text-14-21-0.3">
            by {writtenBy}, {formattedDate}
          </p>
        </div>
        <div
          className={`container flex flex-col ${classnames({
            'xl:flex-col items-center': isFullWidth,
            'xl:flex-row': !isFullWidth,
          })}`}
        >
          <div
            className={classnames({
              'xl:w-full': isFullWidth,
              'xl:pr-[24px] xl:w-[60.5%] pb-[29px] md:pb-0 lg:border-r border-beige-dark':
                !isFullWidth,
            })}
            itemScope
            itemType="https://schema.org/Article"
          >
            <div className="flex flex-col pb-[34px] lg:pb-[34px]">
              {image?.url && (
                <div className="relative min-w-full">
                  <Image
                    itemProp="image"
                    alt={title}
                    {...getMainImageProps(image)}
                    layout="responsive"
                    placeholder="blur"
                    loading="eager"
                    priority
                  />
                </div>
              )}
            </div>
            {pageContent && (
              <ErrorBoundary>
                <div itemProp="description md:pb-[31px] lg:pb-0">
                  <PageBuilderContentParser data={pageContent} />
                </div>
              </ErrorBoundary>
            )}
          </div>
          <div
            className={` border-beige-dark ${classnames({
              'xl:w-3/5': isFullWidth,
              'xl:border-l xl:w-[39.5%]': !isFullWidth,
            })}`}
          >
            <LineBetweenSections hideLine="lg:hidden" />
            <div className="xl:pl-[24px] pt-[15px] lg:pt-0 lg:pb-[117px]">
              <ContactUs
                experts={experts}
                agent={commonAgent}
                imageSizes="!w-[151px] !h-[151px] md:!w-[116px] md:!h-[116px]"
                flagSizes="md:w-[240px]"
                customGap="gap-[8px] md:gap-[54px]"
                infoBlockClasses="md:flex-col md:gap-[44px] lg:gap-0 lg:flex-row"
                expertNameFonts="md:text-16-21-0.3"
                buttonClasses="md:!w-[306px] md:h-[63px]"
                showSecondAgent
              />
            </div>
            {popularBlogs && !!popularBlogs.length && (
              <PopularArticles articles={popularBlogs} />
            )}
          </div>
        </div>
      </section>
      <InsidersGuides />
      <LineBetweenSections hideLine={'container !pb-[4px] !pt-[78px]'} />
      <ErrorBoundary>
        <RelatedArticles
          name="Latest Articles"
          showButtonMore="hidden mb-[67px]"
        >
          {blogs?.map((blog) => {
            const image = flattenStrapiDataItem(blog?.mainImage?.data);

            return (
              <Articles
                href={`/blog/${blog.slug}`}
                key={blog.id}
                topics={blog?.blogTags}
                imageUrl={getSmallImageUrlFromImage(image)}
                blurredImageUrl={getThumbnailImageUrlFromImage(image)}
                imageAlt={blog.title}
                title={blog.title}
                description={blog.shortDescription || ''}
              />
            );
          })}
        </RelatedArticles>
      </ErrorBoundary>
      <SignUpSection />
    </>
  );
};

export default Article;

export const getStaticProps: GetStaticProps<ArticleInterface> = async ({
  params,
}) => {
  const slug = params?.urlArticle as string;

  const [articleResponseResult, defaultAgentResponse, expertsResponse] =
    await Promise.all([
      client.query<{ blogs: BlogsResponse }>({
        query: LOAD_ARTICLE,
        variables: {
          slug: slug,
        },
        fetchPolicy: 'no-cache',
      }),
      client.query<{
        defaultAgent: DefaultAgentsResponse;
      }>({
        query: LOAD_COMMON_AGENT,
      }),
      client.query<{ agents: AgentsResponse }>({
        query: LOAD_AGENTS_FOR_EXPERTS_SECTION,
      }),
    ]);

  const blogsResponseResult = await client.query<{ blogs: BlogsResponse }>({
    query: LOAD_BLOGS,
    variables: {
      limit: 6,
      start: 0,
      slug: articleResponseResult?.data?.blogs?.data[0]?.attributes?.slug,
    },
  });

  const urlIsCorrect =
    slug === articleResponseResult?.data?.blogs?.data[0]?.attributes?.slug;

  if (!urlIsCorrect) return notfoundRedirect;

  return {
    props: {
      commonAgent: flattenStrapiDataItem(
        defaultAgentResponse.data?.defaultAgent.data
      ).common,
      article: flattenStrapiDataItem(articleResponseResult.data?.blogs.data[0]),
      blogs: flattenStrapiBulkDataItems(blogsResponseResult.data?.blogs.data),
      experts: flattenStrapiBulkDataItems(expertsResponse.data.agents?.data),
    },
    revalidate: DEFAULT_REVALIDATE_INTERVAL,
  };
};

export async function getStaticPaths() {
  // const pageSize = 100;
  // let page = 1;
  // let finished = false;
  // let result: Entity<BlogWithoutId>[] = [];

  // while (!finished) {
  //   const { data } = await client.query<{
  //     blogs: ResponseCollection<BlogWithoutId>;
  //   }>({
  //     query: BLOG_ITEMS_LIST,
  //     variables: { pageSize, page },
  //     fetchPolicy: 'no-cache',
  //   });

  //   result = result.concat(data.blogs.data);
  //   page += 1;

  //   if (data.blogs.data.length < pageSize) {
  //     finished = true;
  //   }
  // }

  // const paths = result
  //   .filter((blog) => blog.attributes.slug)
  //   .map((blog) => ({
  //     params: { urlArticle: blog.attributes.slug },
  //   }));

  return { paths: [], fallback: 'blocking' };
}
